import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { getCandidateSession, requestPasswordReset } from "@/lib/candidate-api";
import { getAdminSession } from "@/lib/api-client";
import { getEmployerSession } from "@/lib/employer-api";
import { unifiedLogin } from "@/lib/unified-login";
import LoginView from "@/components/cl/views/LoginView";

export const Route = createFileRoute("/login")({
  head: () => {
    const t = "Log in | CLOSDD";
    const d = "One log in for everyone on CLOSDD: candidates, employers and admin. Use your email or username and password.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: UnifiedLogin,
});

type ViewName = "login" | "forgot" | "sent";

function UnifiedLogin() {
  const navigate = useNavigate();
  const [who, setWho] = useState<"seller" | "employer">("seller");
  const [view, setView] = useState<ViewName>("login");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  // Already logged in? Go straight to that account's dashboard.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getCandidateSession(), getEmployerSession(), getAdminSession()]).then(
      ([candidate, employer, admin]) => {
        if (cancelled) return;
        if (candidate) return void navigate({ to: "/dashboard" });
        if (employer) return void navigate({ to: "/employer" });
        if (admin) return void navigate({ to: "/admin/{-$tab}" });
        setChecking(false);
      },
    ).catch(() => {
      // A failed session lookup must never leave the form hidden.
      if (!cancelled) setChecking(false);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const seller = who === "seller";
  const tabBase =
    "flex:1 1 0;min-width:0;min-height:44px;padding:0 12px;border:0;border-radius:999px;font:inherit;font-size:15px;font-weight:700;cursor:pointer;";
  const onTab = tabBase + "background:#0B2160;color:#FFFFFF";
  const offTab = tabBase + "background:transparent;color:#3A3A40";

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    // Read by id: the salesperson/employer tab buttons sit inside this form too.
    const field = (name: string) => e.currentTarget.querySelector<HTMLInputElement>(`#${name}`)?.value ?? "";
    const id = field("li-email").trim().toLowerCase();
    const pw = field("li-pass").trim();
    setError("");
    setBusy(true);
    try {
      const user = await unifiedLogin(id, pw);
      const dashboardByRole = { candidate: "/dashboard", employer: "/employer", admin: "/admin/{-$tab}" } as const;
      await navigate({ to: dashboardByRole[user.role] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Those details are not correct.");
    } finally {
      setBusy(false);
    }
  }

  async function sendReset(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = (e.currentTarget.querySelector<HTMLInputElement>("#li-reset")?.value ?? "").trim();
    setError("");
    try {
      await requestPasswordReset(email);
    } catch {
      // The copy says "if there's an account", so a failure reads the same.
    }
    setView("sent");
  }

  if (checking) return <div style={{ minHeight: "100vh", background: "#FFFFFF" }} />;

  return (
    <>
      <LoginView
        v={{
          isSeller: seller,
          isEmployer: !seller,
          sellerPressed: seller ? "true" : "false",
          employerPressed: seller ? "false" : "true",
          sellerTab: seller ? onTab : offTab,
          employerTab: seller ? offTab : onTab,
          pickSeller: () => setWho("seller"),
          pickEmployer: () => setWho("employer"),
          formTitle: seller ? "Log in to your profile" : "Log in to your employer account",
          emailLabel: "Email or username",
          emailPlaceholder: "Email or username",
          pwType: showPw ? "text" : "password",
          pwLabel: showPw ? "Hide" : "Show",
          pwAria: showPw ? "Hide password" : "Show password",
          togglePw: () => setShowPw((s) => !s),
          showLogin: view === "login",
          showForgot: view === "forgot",
          showResetSent: view === "sent",
          showDone: false,
          forgot: () => setView("forgot"),
          back: () => {
            setError("");
            setView("login");
          },
          submit,
          sendReset,
          doneText: "",
          doneHref: "/",
          doneCta: "",
        }}
      />
      {error || busy ? (
        <p role="alert" style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", background: "#FFFFFF", color: error ? "#B3261E" : "#0B2160", border: "1px solid #E3E3DE", borderRadius: 8, padding: "12px 18px", fontFamily: "Archivo, system-ui, sans-serif", fontWeight: 700 }}>
          {error || "Logging in…"}
        </p>
      ) : null}
    </>
  );
}
